import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import "mapbox-gl/dist/mapbox-gl.css";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import LoadingModal from "../../common/components/LoadingModal";
import { get, post, unwrapError } from "../../common/utils/api";
import {
  addSentinel2Tiles,
  getBounds,
  mapboxDrawStyle,
  setSentinel2Visibility,
} from "../../common/utils/map.js";
import {
  createMap,
  orderDelineationLayers,
  setHighResolutionVisibility,
  showTask,
  updateLayer,
} from "../utils/map";

function DelineationTask({ showAlert, t }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [scenes, setScenes] = useState([]);
  const [sceneVisible, setSceneVisible] = useState(true);
  const [highResolutionVisible, setHighResolutionVisible] = useState(true);
  const [currentScene, setCurrentScene] = useState();

  const [task, setTask] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const draw = useMemo(() => {
    return new MapboxDraw({
      displayControlsDefault: false,
      // Select which mapbox-gl-draw control buttons to add to the map.
      controls: {
        polygon: true,
        trash: true,
      },
      // Set mapbox-gl-draw to draw by default.
      // The user does not have to click the polygon control button first.
      defaultMode: "draw_polygon",
      userProperties: true,
      styles: mapboxDrawStyle,
    });
  }, []);

  function updateScenes(bbox, date, country) {
    const startDate = moment(date).subtract(1, "months").toISOString();
    const endDate = moment(date).add(1, "months").toISOString();

    var search_query = {
      limit: 200,
      bbox: bbox,
    };
    var href;

    if (country == "united_states_of_america") {
      search_query = {
        ...search_query,
        collections: ["naip"],
      };
      href = "https://planetarycomputer.microsoft.com/api/stac/v1/search";
    } else {
      search_query = {
        ...search_query,
        datetime: `${startDate}/${endDate}`,
        collections: ["super-resolution-sentinel2_l2a_cog"],
      };
      href = "https://stac-server-dev.groundtruthanalytics.com/search";
    }

    const backupQuery = {
      ...search_query,
      collections: ["sentinel-2-l2a"],
    };
    const backupHref = "https://earth-search.aws.element84.com/v1/search";

    function search(href, query) {
      return fetch(href, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(query),
      }).then((res) => res.json());
    }

    async function runSearch() {
      let data = await search(href, search_query);

      if (!data.features || data.features.length === 0) {
        console.log("Super resolution search empty, try sentinel-2...");
        data = await search(backupHref, backupQuery);
      }

      var scenes = data.features.map((f) => {
        return {
          date: f.properties.start_datetime || f.properties.datetime,
          scene_id: f.id,
        };
      });
      // Ascending
      scenes.sort(function (a, b) {
        return new Date(a.date) - new Date(b.date);
      });

      // Find closest date to input
      const targetDate = moment(date).toDate();
      let closestIndex;
      let closestDiff = Infinity;
      scenes.forEach((d, i) => {
        const diff = Math.abs(new Date(d.date) - targetDate);
        if (diff < closestDiff) {
          closestIndex = i;
          closestDiff = diff;
        }
      });
      setScenes(scenes);
      setCurrentScene(closestIndex);
      setSceneVisible(true);
    }

    runSearch().catch(console.error);
  }

  useEffect(() => {
    if (mapLoaded) {
      setSentinel2Visibility(map.current, sceneVisible);
    }
  }, [sceneVisible, mapLoaded]);

  useEffect(() => {
    if (mapLoaded) {
      setHighResolutionVisibility(map.current, highResolutionVisible);
    }
  }, [highResolutionVisible, mapLoaded]);

  useEffect(() => {
    if (mapContainer.current === null) return; // need map container to be showing
    if (map.current) return; // initialize map only once
    map.current = createMap(mapContainer.current, task);

    map.current.on("load", () => {
      map.current.on("draw.create", () => {
        // Setting the mode back to draw immediately causes the current shape
        // not to finish, so wait a bit
        setTimeout(() => draw.changeMode("draw_polygon"), 100);
      });

      map.current.addControl(draw);

      // Dummy layer for tracking the end of the raster layers
      const layers = map.current.getStyle().layers;
      let beforeLayer;
      for (let i = 0; i < layers.length - 1; i++) {
        if (layers[i].id == "satellite") {
          beforeLayer = layers[i + 1].id;
        }
      }
      map.current.addSource("empty", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.current.addLayer(
        {
          id: "after-satellite",
          type: "symbol",
          source: "empty",
        },
        beforeLayer,
      );
      setMapLoaded(true);
    });
  });

  async function submit() {
    const parcels = [];
    draw.getAll().features.forEach((f) => {
      // There seems to be a phantom geometry last in the feature collection
      // that is 1-2 coordinates in the same place, ignore this one
      if (f.geometry.coordinates[0].length > 2) {
        parcels.push({ data: f });
      }
    });

    const payload = {
      longitude: task.longitude,
      latitude: task.latitude,
      width: task.width,
      date: task.date,
      country: task.country,
      parcel2_set: parcels,
    };
    return post("/parcels2/delineation_task/submit/", payload)
      .then(() => {
        draw.deleteAll();
        draw.changeMode("draw_polygon");
        showAlert({
          type: "success",
          message: t("common.successfullySubmitted", "Successfully submitted"),
        });
      })
      .catch(function (error) {
        showAlert({ type: "error", message: unwrapError(error) });
      });
  }

  const getTask = async () => {
    return get(
      `/parcels2/delineation_task/?country=${selectedCountry.toLowerCase()}`,
    ).then((response) => {
      if (response.data) {
        document.getElementById("map").style.display = "block";
        return response.data;
      } else {
        document.getElementById("map").style.display = "none";
        return null;
      }
    });
  };

  useEffect(() => {
    get("/users/groups/").then((response) => {
      const countries = response.data;
      setCountryOptions(countries);
    });
  }, []);

  useEffect(() => {
    if (countryOptions.length === 0) return;

    const countryFromUrl = searchParams.get("country");
    const matchingCountry = countryOptions.find(
      (country) => country.toLowerCase() === countryFromUrl?.toLowerCase(),
    );
    const country = matchingCountry || countryOptions[0];

    setSelectedCountry(country);

    const normalizedCountry = country.toLowerCase();
    if (countryFromUrl !== normalizedCountry) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("country", normalizedCountry);
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [countryOptions, searchParams, setSearchParams]);

  function selectCountry(country) {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("country", country.toLowerCase());
    setSearchParams(nextSearchParams);
  }

  const next = async (skip) => {
    setIsUpdating(true);
    if (task && !skip) {
      await submit();
    }

    const t = await getTask();
    setTask(t);
    if (t) {
      const bounds = getBounds(t);
      updateScenes(bounds.flat(), t.date, t?.country);
    }
    updateLayer(map.current, t?.country);

    setIsUpdating(false);
  };

  function refreshTask() {
    if (!mapLoaded) return;
    if (!task) return;

    showTask(map.current, task);

    // Draw the central point
    const newData = {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [task.longitude, task.latitude],
          },
        },
      ],
    };
    const geojsonSource = map.current.getSource("crops");
    if (geojsonSource !== undefined) {
      // Update the data after the GeoJSON source was created
      geojsonSource.setData(newData);
    } else {
      // Create GeoJSON source
      map.current.addSource("crops", {
        type: "geojson",
        data: newData,
      });

      map.current.addLayer({
        id: "circle",
        type: "circle",
        source: "crops",
        paint: {
          "circle-color": "#4264fb",
          "circle-radius": 5,
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
          "circle-opacity": 0.2,
        },
      });
    }
  }

  useEffect(() => {
    if (mapLoaded && selectedCountry) {
      next(true);
    }
  }, [mapLoaded, selectedCountry]);
  useEffect(refreshTask, [task, mapLoaded]);

  const isEnd = currentScene === scenes.length - 1;
  function previousScene() {
    if (currentScene !== 0) {
      setCurrentScene(currentScene - 1);
      setSceneVisible(true);
    }
  }
  function nextScene() {
    if (!isEnd) {
      setCurrentScene(currentScene + 1);
      setSceneVisible(true);
    }
  }

  useEffect(() => {
    if (mapLoaded && currentScene !== undefined) {
      const sceneId = scenes[currentScene].scene_id;
      addSentinel2Tiles(map.current, sceneId);
      orderDelineationLayers(map.current);
    }
  }, [mapLoaded, currentScene, scenes]);

  return (
    <React.Fragment>
      <LoadingModal open={isUpdating} />
      <div className="border-b border-gray-200 bg-white px-3 py-3 sm:px-6 rounded-md">
        <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="ml-4 mt-2">
            <h3 className="text-md font-medium leading-6 text-gray-900">
              Click the map to draw polygons that correspond to plots
            </h3>
          </div>
          {countryOptions.length > 0 && (
            <div className="ml-4 mt-2 flex items-baseline">
              <label htmlFor="country">Country:</label>
              <select
                id="country"
                value={selectedCountry || ""}
                className="ml-2 max-w-lg rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:max-w-xs sm:text-sm"
                onChange={(e) => selectCountry(e.target.value)}
              >
                {countryOptions.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </select>
            </div>
          )}
          {task !== null && (
            <div className="ml-4 mt-2 flex-shrink-0">
              <div className="flex items-baseline">
                <div className="flex">
                  {task &&
                    `Location (${task.longitude.toFixed(
                      4,
                    )}, ${task.latitude.toFixed(4)})`}
                </div>
                <button
                  type="button"
                  onClick={() => next(true)}
                  className="inline-flex items-center rounded-md border border-transparent border-gray-300 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:bg-gray-50 ml-4"
                >
                  Skip
                </button>
                <button
                  type="button"
                  onClick={() => next(false)}
                  className="inline-flex items-center rounded-md border border-transparent bg-cyan-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 ml-4"
                >
                  Submit
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-6">
        <div
          id="map"
          ref={mapContainer}
          className="map-container aspect-square max-h-[75vh] mx-auto"
        ></div>
        {task !== null && (
          <div className="mt-4 flex items-center justify-center space-x-2">
            <label htmlFor="high-resolution-visible">
              Show high resolution image ({task.date.substring(0, 10)}):
            </label>
            <input
              id="high-resolution-visible"
              name="high-resolution-visible"
              type="checkbox"
              checked={highResolutionVisible}
              onChange={() => setHighResolutionVisible((visible) => !visible)}
              className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
            />
          </div>
        )}
        {task === null && (
          <div className="mt-12 text-center text-xl font-semibold tracking-tight text-gray-900">
            No parcel for delineation
          </div>
        )}
        {scenes.length > 0 && (
          <div className="text-center">
            <div className="mt-8 border-b border-gray-200 bg-white px-3 py-3 sm:px-6 rounded-md inline-flex">
              <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-center sm:flex-nowrap">
                <div className="ml-4 mt-2 flex items-center">
                  Real-time satellite image date:
                  <div className="flex items-center ml-2">
                    <div>
                      <button
                        type="button"
                        onClick={previousScene}
                        disabled={currentScene === 0}
                        className="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                      >
                        <span className="sr-only">Previous</span>

                        <ChevronLeftIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    <div className="flex justify-center">
                      <div className="relative border-y border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-500 focus:z-20 w-32 text-center">
                        {moment(scenes[currentScene].date).format("YYYY-MM-DD")}
                      </div>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={nextScene}
                        disabled={isEnd}
                        className="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20 disabled:opacity-50"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRightIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="ml-8 mt-2 flex-shrink-0">
                  <div className="flex items-baseline space-x-2">
                    <div className="flex">Show real-time satellite image:</div>
                    <input
                      id="comments"
                      aria-describedby="comments-description"
                      name="comments"
                      type="checkbox"
                      checked={sceneVisible}
                      onChange={() => setSceneVisible((visible) => !visible)}
                      className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(DelineationTask);
